import Scheduling from "utils/Scheduling";
import {
  buildStoredVisualLayout,
  resolveCanvasLayoutFromConfig,
  resolveRuntimeLayoutFromConfig,
} from "utils/automationSequenceLayoutCoords";
import {
  QUICK_LAUNCH_OPEN_TARGETS,
  QUICK_LAUNCH_TYPES,
  getQuickLaunchTypeLabel,
  normalizeQuickLaunchOpenTarget,
  normalizeQuickLaunchResource,
} from "utils/quickLaunchResources";
import {
  BIRTHDAY_AUDIO_SOURCE_TYPES,
  normalizeBirthdaySoundConfig,
  resolveBirthdayAudioSource,
} from "utils/birthdayAudioConfig";

export const AUTOMATION_SEQUENCE_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
];

export const AUTOMATION_SEQUENCE_TRIGGER_MODES = [
  { value: "classRelative", label: "Class Relative" },
  { value: "dateRules", label: "Custom Time" },
  { value: "fixed", label: "Scheduled Routine" },
];

export const AUTOMATION_SEQUENCE_STEP_TYPES = {
  WAIT: "wait",
  MUSIC: "music",
  COUNTDOWN: "countdown",
  REMINDER: "reminder",
  BIRTHDAY: "birthday",
  RESOURCE: "resource",
};

export const MAX_CONCURRENT_SEQUENCE_STEPS_PER_VISUAL_TYPE = 4;
export const MAX_RESOURCE_STEPS_PER_SEQUENCE = 2;

export const AUTOMATION_SEQUENCE_RESOURCE_TYPES = [
  QUICK_LAUNCH_TYPES.LINK,
  QUICK_LAUNCH_TYPES.PDF,
];

export const CONCURRENT_VISUAL_STEP_TYPES = [
  AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN,
  AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER,
  AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE,
];

export const AUTOMATION_SEQUENCE_TIMELINE_LANES = [
  {
    type: AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC,
    label: "Music",
  },
  {
    type: AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN,
    label: "Countdown",
  },
  {
    type: AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER,
    label: "Reminder",
  },
  {
    type: AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY,
    label: "Birthday",
  },
  {
    type: AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE,
    label: "Resource",
  },
];

export const STEP_LIBRARY = [
  {
    type: AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC,
    label: "Music",
    summary: "Start a playlist or music segment.",
    icon: "fa fa-music",
    tone: "music",
  },
  {
    type: AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN,
    label: "Countdown",
    summary: "Launch a countdown timer on screen.",
    icon: "fa fa-clock-o",
    tone: "countdown",
  },
  {
    type: AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER,
    label: "Reminder",
    summary: "Show a timed message popup.",
    icon: "fa fa-bell",
    tone: "reminder",
  },
  {
    type: AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY,
    label: "Birthday",
    summary: "Celebrate student birthdays during a routine.",
    icon: "fa fa-birthday-cake",
    tone: "birthday",
  },
  {
    type: AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE,
    label: "Resource",
    summary: "Open a website or PDF at a set time.",
    icon: "fa fa-external-link",
    tone: "resource",
  },
];

export const FONT_STYLE_OPTIONS = [
  { value: "helvetica", label: "Helvetica" },
  { value: "arial", label: "Arial" },
  { value: "times-new-roman", label: "Times New Roman" },
  { value: "georgia", label: "Georgia" },
  { value: "verdana", label: "Verdana" },
];

export const MODAL_LOCATION_OPTIONS = [
  { value: "left-top", label: "Left Top" },
  { value: "center-top", label: "Center Top" },
  { value: "right-top-auto-music", label: "Right Top" },
  { value: "left-middle", label: "Left Middle" },
  { value: "center-middle", label: "Center Middle" },
  { value: "right-middle-auto-music", label: "Right Middle" },
  { value: "left-bottom", label: "Left Bottom" },
  { value: "center-bottom", label: "Center Bottom" },
  { value: "right-bottom-auto-music", label: "Right Bottom" },
  { value: "full-screen", label: "Fullscreen" },
];

export const REMINDER_ALERT_SOUND_OPTIONS = [
  { value: "none", label: "None" },
  { value: "short", label: "Alarm - Short" },
  { value: "medium", label: "Alarm - Medium" },
  { value: "long", label: "Alarm - Long" },
  { value: "atmosphere", label: "Atmosphere" },
  { value: "bells", label: "Bells" },
  { value: "belltree", label: "Bell Tree" },
  { value: "blip", label: "Blip" },
  { value: "cavaquinho", label: "Cavaquinho" },
  { value: "ding", label: "Ding" },
  { value: "guitar", label: "Guitar" },
  { value: "harp1", label: "Harp 1" },
  { value: "harp2", label: "Harp 2" },
  { value: "levelcomplete", label: "Level Complete" },
  { value: "mallet1", label: "Mallet 1" },
  { value: "mallet2", label: "Mallet 2" },
  { value: "powerup", label: "Power Up" },
  { value: "sparks", label: "Sparks" },
  { value: "techno", label: "Techno" },
  { value: "triangle", label: "Triangle" },
  { value: "vibes", label: "Vibes" },
];

export const COUNTDOWN_ALERT_SOUND_OPTIONS = [
  { value: "none", label: "None" },
  { value: "atmosphere", label: "Atmosphere" },
  { value: "bells", label: "Bells" },
  { value: "belltree", label: "Bell Tree" },
  { value: "blip", label: "Blip" },
  { value: "cavaquinho", label: "Cavaquinho" },
  { value: "ding", label: "Ding" },
  { value: "guitar", label: "Guitar" },
  { value: "harp1", label: "Harp 1" },
  { value: "harp2", label: "Harp 2" },
  { value: "levelcomplete", label: "Level Complete" },
  { value: "mallet1", label: "Mallet 1" },
  { value: "mallet2", label: "Mallet 2" },
  { value: "powerup", label: "Power Up" },
  { value: "sparks", label: "Sparks" },
  { value: "techno", label: "Techno" },
  { value: "triangle", label: "Triangle" },
  { value: "vibes", label: "Vibes" },
];

export const TIMELINE_SNAP_SECONDS = 1;
export const TIMELINE_MIN_DURATION_SECONDS = 15;
export const TIMELINE_MIN_PIXELS_PER_SECOND = 600 / 900;
export const TIMELINE_MAX_PIXELS_PER_SECOND = 600 / 120;
export const TIMELINE_DEFAULT_PIXELS_PER_SECOND = 2;
export const TIMELINE_OPEN_ENDED_DISPLAY_SECONDS = 300;
export const TIMELINE_MAX_ABSOLUTE_OFFSET_SECONDS = 1800;
/** Soft warning when an end-of-class routine action starts this far after dismissal. */
export const LATE_AFTER_CLASS_END_WARNING_SECONDS = 5 * 60;

/** First block of each type from the timeline lane rail: T-4:00, T-2:00, T-1:00, T+3:00, T+0:00 */
export const getDefaultTimelineRailAddStartOffsetSeconds = (stepType) => {
  switch (stepType) {
    case AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC:
      return -4 * 60;
    case AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN:
      return -2 * 60;
    case AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER:
      return -1 * 60;
    case AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY:
      return 3 * 60;
    case AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE:
      // Place at class start so the new clip lands in the default centered viewport.
      return 0;
    default:
      return 0;
  }
};

export const AUTOMATION_SEQUENCE_LAYOUT_CANVAS = {
  width: 1280,
  height: 720,
};

const createId = (prefix) =>
  `${prefix}-${typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`}`;

const cloneJson = (value) => JSON.parse(JSON.stringify(value));

const deepFreeze = (value) => {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }

  Object.values(value).forEach(deepFreeze);
  return Object.freeze(value);
};

const SHARED_BLANK_SCHEDULE = deepFreeze(cloneJson(Scheduling.blankSchedule));

const cloneBlankSchedule = () => cloneJson(Scheduling.blankSchedule);

const normalizeTimeValue = (timeValue) => {
  if (typeof timeValue !== "string" || !timeValue.includes(":")) return null;
  const [hours, minutes, seconds] = timeValue.split(":");
  if (hours === undefined || minutes === undefined) return null;
  return seconds !== undefined ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}:00`;
};

const normalizeDateRules = (rules = []) =>
  (Array.isArray(rules) ? rules : [])
    .filter((rule) => rule && rule.id)
    .map((rule) => ({
      id: String(rule.id),
      type: rule?.type === "weekdayRecurring" ? "weekdayRecurring" : "specificDates",
      dates: Array.from(new Set((Array.isArray(rule?.dates) ? rule.dates : []).filter(Boolean))).sort(),
      weekdays: Array.from(
        new Set(
          (Array.isArray(rule?.weekdays) ? rule.weekdays : [])
            .map((weekday) => Number(weekday))
            .filter((weekday) => Number.isInteger(weekday) && weekday >= 0 && weekday <= 6)
        )
      ).sort((first, second) => first - second),
      times: Array.from(
        new Set(
          (Array.isArray(rule?.times) ? rule.times : [])
            .map(normalizeTimeValue)
            .filter(Boolean)
        )
      ).sort(),
      active: rule?.active !== false,
    }));

const normalizeDateKey = (value) => {
  if (typeof value !== "string") return "";
  const trimmedValue = value.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmedValue) ? trimmedValue : "";
};

export const normalizeBlackoutDateConfig = (config = {}) => {
  const dates = Array.from(
    new Set((Array.isArray(config?.dates) ? config.dates : []).map(normalizeDateKey).filter(Boolean))
  ).sort();
  const ranges = (Array.isArray(config?.ranges) ? config.ranges : [])
    .map((range) => {
      const startDate = normalizeDateKey(range?.startDate);
      const endDate = normalizeDateKey(range?.endDate);
      if (!startDate && !endDate) return null;
      const normalizedStartDate = startDate || endDate;
      const normalizedEndDate = endDate || startDate;
      return normalizedStartDate <= normalizedEndDate
        ? { startDate: normalizedStartDate, endDate: normalizedEndDate }
        : { startDate: normalizedEndDate, endDate: normalizedStartDate };
    })
    .filter(Boolean)
    .sort((leftRange, rightRange) =>
      leftRange.startDate === rightRange.startDate
        ? leftRange.endDate.localeCompare(rightRange.endDate)
        : leftRange.startDate.localeCompare(rightRange.startDate)
    );

  return { dates, ranges };
};

export const hasBlackoutDateConfig = (config = {}) => {
  const normalizedConfig = normalizeBlackoutDateConfig(config);
  return normalizedConfig.dates.length > 0 || normalizedConfig.ranges.length > 0;
};

export const isDateKeyBlackout = (dateKey, config = {}) => {
  const normalizedDateKey = normalizeDateKey(dateKey);
  if (!normalizedDateKey) return false;
  const normalizedConfig = normalizeBlackoutDateConfig(config);
  return normalizedConfig.dates.includes(normalizedDateKey) ||
    normalizedConfig.ranges.some((range) => normalizedDateKey >= range.startDate && normalizedDateKey <= range.endDate);
};

export const normalizeClassRelativeFilter = (filter = {}) => {
  const allowedTemplateIds = Array.from(
    new Set((Array.isArray(filter?.allowedTemplateIds) ? filter.allowedTemplateIds : []).filter(Boolean).map(String))
  ).sort();
  const scheduleMode = filter?.scheduleMode === "selected" ? "selected" : "all";

  return {
    scheduleMode,
    allowedTemplateIds: scheduleMode === "selected" ? allowedTemplateIds : [],
    blackoutDates: normalizeBlackoutDateConfig(filter?.blackoutDates),
  };
};

const normalizeClassRelativeFilters = (filters = {}, selectedClassIds = []) => {
  const selectedClassIdSet = new Set((Array.isArray(selectedClassIds) ? selectedClassIds : []).map(String));
  return Object.entries(filters && typeof filters === "object" ? filters : {}).reduce((nextFilters, [classId, filter]) => {
    const normalizedClassId = String(classId || "");
    if (!normalizedClassId || !selectedClassIdSet.has(normalizedClassId)) {
      return nextFilters;
    }

    nextFilters[normalizedClassId] = normalizeClassRelativeFilter(filter);
    return nextFilters;
  }, {});
};

const TIMING_OVERRIDE_FIELDS = ["startOffsetSeconds", "durationSeconds"];

const isPlainObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

export const normalizeStepClassOverrides = (overrides = {}) =>
  Object.entries(isPlainObject(overrides) ? overrides : {}).reduce((nextOverrides, [classId, override]) => {
    const normalizedClassId = String(classId || "");
    if (!normalizedClassId || !isPlainObject(override)) {
      return nextOverrides;
    }

    const configOverrides = isPlainObject(override.config)
      ? Object.entries(override.config).reduce((nextConfig, [field, value]) => {
        if (value !== undefined) {
          nextConfig[field] = value;
        }
        return nextConfig;
      }, {})
      : {};
    const timingOverrides = TIMING_OVERRIDE_FIELDS.reduce((nextTiming, field) => {
      if (Number.isFinite(Number(override?.timing?.[field]))) {
        nextTiming[field] = Number(override.timing[field]);
      }
      return nextTiming;
    }, {});

    if (Object.keys(configOverrides).length || Object.keys(timingOverrides).length) {
      nextOverrides[normalizedClassId] = {
        ...(Object.keys(configOverrides).length ? { config: configOverrides } : {}),
        ...(Object.keys(timingOverrides).length ? { timing: timingOverrides } : {}),
      };
    }

    return nextOverrides;
  }, {});

export const hasStepClassOverrides = (step = {}) =>
  Object.keys(normalizeStepClassOverrides(step?.classOverrides)).length > 0;

export const hasStepClassTimingOverrides = (step = {}) =>
  Object.values(normalizeStepClassOverrides(step?.classOverrides)).some((override) =>
    Object.keys(override?.timing || {}).length > 0
  );

const getActiveScheduleEntryCount = (schedule = {}) =>
  Object.values(schedule || {}).reduce((weekCount, weekValue) => {
    const dayCount = Object.values(weekValue || {}).filter((entry) => entry?.active).length;
    return weekCount + dayCount;
  }, 0);

const normalizeAutomationSequenceResourceType = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return QUICK_LAUNCH_TYPES.LINK;
  }
  return AUTOMATION_SEQUENCE_RESOURCE_TYPES.includes(rawValue) ? rawValue : "";
};

export const normalizeAutomationSequenceResourceConfig = (config = {}) => {
  const resourceType = normalizeAutomationSequenceResourceType(config?.resourceType || config?.type);
  const title = String(config?.title || config?.name || "").trim();
  const url = String(config?.url || "").trim();
  const openTarget = normalizeQuickLaunchOpenTarget(config?.openTarget);
  const closeMode = config?.closeMode === "manual" ? "manual" : "afterDuration";
  const normalizedResource = resourceType && url
    ? normalizeQuickLaunchResource({
      id: config?.resourceId || config?.id || "automation-resource-preview",
      type: resourceType,
      title,
      url,
      openTarget,
    })
    : null;

  return {
    ...config,
    resourceType,
    title: normalizedResource?.title || title,
    url: normalizedResource?.url || url,
    openTarget: resourceType === QUICK_LAUNCH_TYPES.LINK
      ? openTarget
      : (config?.openTarget === undefined ? QUICK_LAUNCH_OPEN_TARGETS.APP : openTarget),
    closeMode,
    duration: Math.max(TIMELINE_MIN_DURATION_SECONDS, Number(config?.duration) || TIMELINE_MIN_DURATION_SECONDS),
  };
};

export const automationSequenceResourceConfigToQuickLaunchResource = (stepOrConfig = {}) => {
  const config = stepOrConfig?.config ? stepOrConfig.config : stepOrConfig;
  const normalizedConfig = normalizeAutomationSequenceResourceConfig(config);

  if (!normalizedConfig.resourceType || !normalizedConfig.url) {
    return null;
  }

  return normalizeQuickLaunchResource({
    id: stepOrConfig?.id || normalizedConfig.resourceId || normalizedConfig.id || "automation-resource",
    type: normalizedConfig.resourceType,
    title: normalizedConfig.title,
    url: normalizedConfig.url,
    openTarget: normalizedConfig.openTarget,
  });
};

const isInAppAutomationSequenceResource = (stepOrConfig = {}) => {
  const resource = automationSequenceResourceConfigToQuickLaunchResource(stepOrConfig);
  return Boolean(resource) && resource.openTarget !== QUICK_LAUNCH_OPEN_TARGETS.NEW_TAB;
};

const getDefaultDurationForType = (type, config = {}) => {
  switch (type) {
    case AUTOMATION_SEQUENCE_STEP_TYPES.WAIT:
      return Math.max(TIMELINE_MIN_DURATION_SECONDS, Number(config?.duration) || 0);
    case AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC:
      if (config?.stopMode === "duration") {
        return Math.max(TIMELINE_MIN_DURATION_SECONDS, Number(config?.duration) || 0);
      }
      return 120;
    case AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN:
    case AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER:
    case AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY:
    case AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE:
      return Math.max(TIMELINE_MIN_DURATION_SECONDS, Number(config?.duration) || 0);
    default:
      return 60;
  }
};

const getExplicitStepStartOffset = (step = {}) => {
  if (Number.isFinite(Number(step?.startOffsetSeconds))) {
    return Number(step.startOffsetSeconds);
  }
  if (Number.isFinite(Number(step?.timeline?.startOffsetSeconds))) {
    return Number(step.timeline.startOffsetSeconds);
  }
  return null;
};

const getExplicitStepDuration = (step = {}) => {
  if (Number.isFinite(Number(step?.durationSeconds))) {
    return Number(step.durationSeconds);
  }
  if (Number.isFinite(Number(step?.timeline?.durationSeconds))) {
    return Number(step.timeline.durationSeconds);
  }
  return null;
};

const getExplicitLanePreference = (step = {}) => {
  if (Number.isInteger(Number(step?.lanePreference))) {
    return Math.max(0, Number(step.lanePreference));
  }
  if (Number.isInteger(Number(step?.timeline?.lanePreference))) {
    return Math.max(0, Number(step.timeline.lanePreference));
  }
  return null;
};

const getExplicitLaneSubPreference = (step = {}) => {
  if (Number.isInteger(Number(step?.laneSubPreference))) {
    return Math.max(0, Number(step.laneSubPreference));
  }
  if (Number.isInteger(Number(step?.timeline?.laneSubPreference))) {
    return Math.max(0, Number(step.timeline.laneSubPreference));
  }
  return null;
};

const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));

const getLayoutSizeDefaultsForType = (type) => {
  switch (type) {
    case AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE:
      return { width: 900, height: 600 };
    case AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC:
      return { width: 600, height: 338 };
    case AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN:
    case AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER:
      return { width: 700, height: 300 };
    case AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY:
      return { width: 780, height: 420 };
    default:
      return { width: 600, height: 300 };
  }
};

const getDefaultLayoutFromPlacement = (type, placement) => {
  const sizeDefaults = getLayoutSizeDefaultsForType(type);
  const rightAlignedX = AUTOMATION_SEQUENCE_LAYOUT_CANVAS.width - sizeDefaults.width - 32;
  const centeredX = Math.round((AUTOMATION_SEQUENCE_LAYOUT_CANVAS.width - sizeDefaults.width) / 2);
  const centeredY = Math.round((AUTOMATION_SEQUENCE_LAYOUT_CANVAS.height - sizeDefaults.height) / 2);

  switch (placement) {
    case "left-top":
      return { x: 32, y: 32, ...sizeDefaults };
    case "center-top":
      return { x: centeredX, y: 32, ...sizeDefaults };
    case "right-top-auto-music":
      return { x: rightAlignedX, y: 32, ...sizeDefaults };
    case "left-middle":
      return { x: 32, y: centeredY, ...sizeDefaults };
    case "center-middle":
      return { x: centeredX, y: centeredY, ...sizeDefaults };
    case "right-middle-auto-music":
      return { x: rightAlignedX, y: centeredY, ...sizeDefaults };
    case "left-bottom":
      return {
        x: 32,
        y: AUTOMATION_SEQUENCE_LAYOUT_CANVAS.height - sizeDefaults.height - 32,
        ...sizeDefaults,
      };
    case "center-bottom":
      return {
        x: centeredX,
        y: AUTOMATION_SEQUENCE_LAYOUT_CANVAS.height - sizeDefaults.height - 32,
        ...sizeDefaults,
      };
    case "right-bottom-auto-music":
      return {
        x: rightAlignedX,
        y: AUTOMATION_SEQUENCE_LAYOUT_CANVAS.height - sizeDefaults.height - 32,
        ...sizeDefaults,
      };
    case "full-screen":
      return {
        x: 64,
        y: 36,
        width: AUTOMATION_SEQUENCE_LAYOUT_CANVAS.width - 128,
        height: AUTOMATION_SEQUENCE_LAYOUT_CANVAS.height - 72,
      };
    default:
      return { x: centeredX, y: centeredY, ...sizeDefaults };
  }
};

/** Minimum layout size when resizing/placing sequence overlays (design canvas pixels). */
export const getAutomationSequenceMinLayoutSize = (type = "") => {
  switch (type) {
    case AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE:
      return { minWidth: 320, minHeight: 240 };
    case AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC:
      return { minWidth: 240, minHeight: 135 };
    case AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY:
      return { minWidth: 320, minHeight: 220 };
    case AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER:
      return { minWidth: 260, minHeight: 96 };
    case AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN:
      return { minWidth: 260, minHeight: 160 };
    default:
      return { minWidth: 260, minHeight: 160 };
  }
};

const getVisualLayoutClampOptions = (type) => {
  const { minWidth, minHeight } = getAutomationSequenceMinLayoutSize(type);

  return {
    minWidth,
    minHeight,
    maxWidth: AUTOMATION_SEQUENCE_LAYOUT_CANVAS.width - 16,
    maxHeight: AUTOMATION_SEQUENCE_LAYOUT_CANVAS.height - 16,
  };
};

const clampStepVisualLayout = (type, layout = {}) => {
  const sizeDefaults = getLayoutSizeDefaultsForType(type);
  const requestedWidth = Math.round(Number(layout?.width) || sizeDefaults.width);
  const requestedHeight = Math.round(Number(layout?.height) || sizeDefaults.height);
  const { minWidth, minHeight, maxWidth, maxHeight } = getVisualLayoutClampOptions(type);
  let width = clamp(requestedWidth, minWidth, maxWidth);
  let height = clamp(requestedHeight, minHeight, maxHeight);

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC) {
    const aspectRatio = 16 / 9;
    height = Math.round(width / aspectRatio);
    if (height > maxHeight) {
      height = maxHeight;
      width = Math.round(height * aspectRatio);
    }
  }

  const canvasRect = {
    x: clamp(Math.round(Number(layout?.x) || 0), 0, Math.max(0, AUTOMATION_SEQUENCE_LAYOUT_CANVAS.width - width)),
    y: clamp(Math.round(Number(layout?.y) || 0), 0, Math.max(0, AUTOMATION_SEQUENCE_LAYOUT_CANVAS.height - height)),
    width,
    height,
    zIndex: Number.isFinite(Number(layout?.zIndex)) ? Number(layout.zIndex) : 1,
  };

  return buildStoredVisualLayout(canvasRect, getVisualLayoutClampOptions(type));
};

const getDefaultVisualLayout = (type, config = {}) => {
  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC) {
    return clampStepVisualLayout(
      type,
      getDefaultLayoutFromPlacement(type, config?.showVideoLocation || "center-middle")
    );
  }

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN) {
    const fallback = config?.position && config?.size
      ? {
        x: config.position.x,
        y: config.position.y,
        width: config.size.width,
        height: config.size.height,
      }
      : getDefaultLayoutFromPlacement(type, config?.modalClassName || "center-middle");
    return clampStepVisualLayout(type, fallback);
  }

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER) {
    const fallback = config?.position && config?.size
      ? {
        x: config.position.x,
        y: config.position.y,
        width: config.size.width,
        height: config.size.height,
      }
      : getDefaultLayoutFromPlacement(type, config?.modalClassName || "center-top");
    return clampStepVisualLayout(type, fallback);
  }

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY) {
    const fallback = config?.position && config?.size
      ? {
        x: config.position.x,
        y: config.position.y,
        width: config.size.width,
        height: config.size.height,
      }
      : getDefaultLayoutFromPlacement(type, config?.modalClassName || "center-middle");
    return clampStepVisualLayout(type, fallback);
  }

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE) {
    return clampStepVisualLayout(type, { x: 80, y: 40, ...getLayoutSizeDefaultsForType(type), zIndex: 20 });
  }

  return null;
};

const normalizeVisualLayoutConfig = (type, config = {}) => {
  const shouldNormalizeResourceLayout = type === AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE
    && isInAppAutomationSequenceResource(config);
  if (
    ![
      AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC,
      AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN,
      AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER,
      AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY,
    ].includes(type)
    && !shouldNormalizeResourceLayout
  ) {
    return config;
  }

  const normalizedLayout = clampStepVisualLayout(type, config?.layout || getDefaultVisualLayout(type, config));
  const nextConfig = {
    ...config,
    layout: normalizedLayout,
  };

  if (
    type === AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN ||
    type === AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER ||
    type === AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY
  ) {
    const { position, size } = resolveRuntimeLayoutFromConfig(
      normalizedLayout,
      getVisualLayoutClampOptions(type)
    );
    nextConfig.position = position;
    nextConfig.size = size;
  }

  return nextConfig;
};

const getTimelineLaneMetaForType = (type) => {
  const laneIndex = AUTOMATION_SEQUENCE_TIMELINE_LANES.findIndex((lane) => lane.type === type);
  if (laneIndex >= 0) {
    return {
      ...AUTOMATION_SEQUENCE_TIMELINE_LANES[laneIndex],
      index: laneIndex,
    };
  }

  return {
    type,
    label: getStepTypeMeta(type).label,
    index: AUTOMATION_SEQUENCE_TIMELINE_LANES.length,
  };
};

const syncConfigDurationWithClipDuration = (type, config = {}, durationSeconds) => {
  const normalizedDuration = Math.max(TIMELINE_MIN_DURATION_SECONDS, Number(durationSeconds) || 0);

  if (
    type === AUTOMATION_SEQUENCE_STEP_TYPES.WAIT ||
    type === AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN ||
    type === AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER ||
    type === AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY ||
    type === AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE
  ) {
    const nextConfig = {
      ...config,
      duration: normalizedDuration,
    };
    if (type === AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY && config?.close !== "manual") {
      nextConfig.close = normalizedDuration;
    }
    return nextConfig;
  }

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC && config?.stopMode === "duration") {
    return {
      ...config,
      duration: normalizedDuration,
    };
  }

  return config;
};

export const createDefaultTrigger = () => ({
  triggerMode: "classRelative",
  schedule: cloneBlankSchedule(),
  dateRules: [],
  relativeConfig: {
    reference: "classStart",
    direction: "before",
    offsetSeconds: 0,
    classIds: [],
    classFilters: {},
    globalBlackoutDates: normalizeBlackoutDateConfig(),
  },
});

export const createDefaultStepConfig = (type, options = {}) => {
  const firstPlaylist = options?.playlists?.[0];

  switch (type) {
    case AUTOMATION_SEQUENCE_STEP_TYPES.WAIT:
      return {
        duration: 5,
        behavior: "Pause before the next step begins.",
      };
    case AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC:
      return normalizeVisualLayoutConfig(type, {
        duration: 240,
        stopMode: "duration",
        name: firstPlaylist?.name || "Auto Music Step",
        playlistId: firstPlaylist?.playlistId || "",
        showVideo: false,
        showVideoLocation: "center-middle",
        showVideoBackdrop: true,
        active: true,
        shuffle: false,
        volume: 1,
      });
    case AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN:
      return normalizeVisualLayoutConfig(type, {
        startTime: "00:00",
        duration: 120,
        closeMode: "afterDuration",
        header: "You must be in your seat by...",
        active: true,
        alertMessage: "Time's up",
        startSound: "none",
        startSoundName: "None",
        alertSound: "belltree",
        alertSoundName: "Bell Tree",
        youtube: false,
        youtubeSrc: "",
        schedule: cloneBlankSchedule(),
        modalClassName: "center-middle",
        backdrop: true,
        flash: false,
        backgroundColor: "#ffffff",
        fontColor: "#000000",
        fontStyle: "helvetica",
        countdownFontColor: "#000000",
        countdownFontStyle: "helvetica",
        useClassStyleDefaults: true,
        customAudio: false,
        customAudioSrc: "",
        customAudioName: "",
        size: { width: 700, height: 300 },
        position: { x: 445, y: 200 },
      });
    case AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER:
      return normalizeVisualLayoutConfig(type, {
        startTime: "00:00",
        duration: 20,
        closeMode: "afterDuration",
        active: true,
        reminderMessage: "Take attendance",
        alertSound: "belltree",
        alertSoundName: "Bell Tree",
        days: [],
        schedule: cloneBlankSchedule(),
        modalClassName: "center-top",
        backdrop: true,
        flash: false,
        backgroundColor: "#ffffff",
        fontColor: "#000000",
        fontStyle: "helvetica",
        useClassStyleDefaults: true,
      });
    case AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY:
      return normalizeVisualLayoutConfig(type, {
        duration: 30,
        active: true,
        background: "1",
        language: "English",
        sound: "none",
        soundName: "None",
        soundSourceType: BIRTHDAY_AUDIO_SOURCE_TYPES.NONE,
        youtube: false,
        youtubeSrc: "",
        customAudio: false,
        customAudioSrc: "",
        customAudioName: "",
        soundStart: "auto",
        close: 30,
        modalClassName: "center-middle",
        backdrop: true,
        backgroundColor: "#ffffff",
        fontColor: "#ffffff",
        fontStyle: "helvetica",
        useClassStyleDefaults: false,
        size: { width: 780, height: 420 },
        position: { x: 405, y: 140 },
      });
    case AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE:
      return normalizeAutomationSequenceResourceConfig({
        resourceType: QUICK_LAUNCH_TYPES.LINK,
        title: "",
        url: "",
        openTarget: QUICK_LAUNCH_OPEN_TARGETS.APP,
        closeMode: "afterDuration",
        duration: TIMELINE_MIN_DURATION_SECONDS,
      });
    default:
      return {};
  }
};

export const getStepTypeMeta = (type) =>
  STEP_LIBRARY.find((stepType) => stepType.type === type) || {
    type,
    label: "Step",
    summary: "",
    icon: "fa fa-circle",
    tone: "default",
  };

export const buildStepTitle = (type) => getStepTypeMeta(type).label;

export const isVisualAutomationStep = (step = {}) => {
  const type = step?.type;
  const config = step?.config || {};

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC) {
    return Boolean(config?.showVideo);
  }

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE) {
    return isInAppAutomationSequenceResource(step);
  }

  return [
    AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN,
    AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER,
    AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY,
  ].includes(type);
};

export const getStepVisualLayout = (step = {}) => {
  const type = step?.type;
  if (!isVisualAutomationStep(step)) {
    return null;
  }

  const config = step?.config || {};
  const layout = config.layout || getDefaultVisualLayout(type, config);
  if (!layout) {
    return null;
  }

  return resolveCanvasLayoutFromConfig(layout, getVisualLayoutClampOptions(type));
};

export const setStepVisualLayout = (step = {}, layoutPatch = {}) => {
  if (!step?.type) {
    return step;
  }

  const nextConfig = normalizeVisualLayoutConfig(step.type, {
    ...(step?.config || {}),
    layout: {
      ...getStepVisualLayout(step),
      ...(layoutPatch || {}),
    },
  });

  return {
    ...step,
    config: {
      ...nextConfig,
      layoutWasEdited: true,
    },
  };
};

export const buildStepSummary = (type, config = {}, options = {}) => {
  const playlistsById = options?.playlistsById || {};

  switch (type) {
    case AUTOMATION_SEQUENCE_STEP_TYPES.WAIT:
      return `Delay for ${Math.max(0, Number(config?.duration) || 0)} sec`;
    case AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC: {
      const playlistLabel = config?.playlistId
        ? playlistsById[config.playlistId]?.name || config?.name || "Selected playlist"
        : "No playlist selected yet...";
      return `Playlist: ${playlistLabel}`;
    }
    case AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN:
      return `${Math.max(0, Number(config?.duration) || 0)} sec countdown`;
    case AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER:
      return config?.reminderMessage || "Show reminder";
    case AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY: {
      const bg = String(config?.background || "1");
      const language = String(config?.language || "English");
      const audioSource = resolveBirthdayAudioSource(config);
      const soundLabel = audioSource.type === BIRTHDAY_AUDIO_SOURCE_TYPES.NONE
        ? ""
        : ` · ${audioSource.label || "Custom song"}`;
      return language && language !== "English"
        ? `Birthday · Background ${bg} · ${language}${soundLabel}`
        : `Birthday · Background ${bg}${soundLabel}`;
    }
    case AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE: {
      const normalizedConfig = normalizeAutomationSequenceResourceConfig(config);
      const resourceTypeLabel = getQuickLaunchTypeLabel(normalizedConfig.resourceType);
      return normalizedConfig.title
        ? `${resourceTypeLabel}: ${normalizedConfig.title}`
        : `Launch ${resourceTypeLabel}`;
    }
    default:
      return getStepTypeMeta(type).summary;
  }
};

export const isStepOpenEnded = (step = {}) => {
  const type = STEP_LIBRARY.some((entry) => entry.type === step?.type)
    ? step.type
    : AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC;
  const config = {
    ...createDefaultStepConfig(type),
    ...(step?.config || {}),
  };

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC) {
    return config?.stopMode !== "duration";
  }

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER) {
    return config?.closeMode === "manual";
  }

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY) {
    return config?.close === "manual";
  }

  if (type === AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE) {
    return config?.closeMode === "manual";
  }

  return false;
};

export const getStepDurationSeconds = (step = {}) => {
  const type = STEP_LIBRARY.some((entry) => entry.type === step?.type)
    ? step.type
    : AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC;
  const config = {
    ...createDefaultStepConfig(type),
    ...(step?.config || {}),
  };
  const explicitDuration = getExplicitStepDuration(step);
  return Math.max(
    TIMELINE_MIN_DURATION_SECONDS,
    explicitDuration == null ? getDefaultDurationForType(type, config) : explicitDuration
  );
};

export const getTimelineClipDurationSeconds = (step = {}, steps = []) => {
  const startOffsetSeconds = getStepStartOffsetSeconds(step);
  const effectiveEndOffsetSeconds = getStepEffectiveEndOffsetSeconds(step, steps);

  if (Number.isFinite(effectiveEndOffsetSeconds)) {
    return Math.max(
      TIMELINE_MIN_DURATION_SECONDS,
      Math.min(TIMELINE_MAX_ABSOLUTE_OFFSET_SECONDS, effectiveEndOffsetSeconds) - startOffsetSeconds
    );
  }

  return Math.max(
    TIMELINE_MIN_DURATION_SECONDS,
    Math.min(
      Math.max(TIMELINE_OPEN_ENDED_DISPLAY_SECONDS, getStepDurationSeconds(step)),
      TIMELINE_MAX_ABSOLUTE_OFFSET_SECONDS - startOffsetSeconds
    )
  );
};

export const getTimelineLaneMeta = (stepOrType) =>
  getTimelineLaneMetaForType(typeof stepOrType === "string" ? stepOrType : stepOrType?.type);

export const getStepStartOffsetSeconds = (step = {}) => {
  const explicitOffset = getExplicitStepStartOffset(step);
  return Number.isFinite(explicitOffset) ? explicitOffset : 0;
};

export const clampTimelineStartOffsetSeconds = (startOffsetSeconds) =>
  Math.max(
    -TIMELINE_MAX_ABSOLUTE_OFFSET_SECONDS,
    Math.min(
      TIMELINE_MAX_ABSOLUTE_OFFSET_SECONDS - TIMELINE_MIN_DURATION_SECONDS,
      Number(startOffsetSeconds) || 0
    )
  );

const getSortedStepsOfSameType = (step = {}, steps = []) => {
  if (!step?.type || !Array.isArray(steps)) {
    return [];
  }

  return [...steps]
    .map((candidateStep, originalIndex) => ({
      step: candidateStep,
      originalIndex,
    }))
    .filter((entry) => entry.step?.type === step.type)
    .sort((leftEntry, rightEntry) => {
      const startDifference = getStepStartOffsetSeconds(leftEntry.step) - getStepStartOffsetSeconds(rightEntry.step);
      if (startDifference !== 0) {
        return startDifference;
      }

      return leftEntry.originalIndex - rightEntry.originalIndex;
    })
    .map((entry) => entry.step);
};

export const getNextStepOfSameType = (step = {}, steps = []) => {
  if (!step?.id) {
    return null;
  }

  const sortedSteps = getSortedStepsOfSameType(step, steps);
  const stepIndex = sortedSteps.findIndex((candidateStep) => candidateStep?.id === step.id);
  if (stepIndex < 0 || stepIndex >= sortedSteps.length - 1) {
    return null;
  }

  return sortedSteps[stepIndex + 1];
};

export const getStepEffectiveEndOffsetSeconds = (step = {}, steps = []) => {
  const startOffsetSeconds = getStepStartOffsetSeconds(step);

  if (!isStepOpenEnded(step)) {
    return startOffsetSeconds + getStepDurationSeconds(step);
  }

  if (step?.type === AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC) {
    const nextMusicStep = getNextStepOfSameType(step, steps);
    if (nextMusicStep) {
      return Math.max(
        getStepStartOffsetSeconds(nextMusicStep),
        startOffsetSeconds + TIMELINE_MIN_DURATION_SECONDS
      );
    }
  }

  return Number.POSITIVE_INFINITY;
};

export const getMaxConcurrentStepsForType = (steps = [], stepType = "") => {
  const events = (Array.isArray(steps) ? steps : [])
    .filter((step) => step?.type === stepType)
    .flatMap((step) => {
      const start = getStepStartOffsetSeconds(step);
      const end = getStepEffectiveEndOffsetSeconds(step, steps);
      if (!Number.isFinite(start) || end <= start) return [];
      return [
        { offsetSeconds: start, delta: 1 },
        { offsetSeconds: end, delta: -1 },
      ];
    })
    .sort((left, right) => {
      const offsetDifference = left.offsetSeconds - right.offsetSeconds;
      if (offsetDifference !== 0) return offsetDifference;
      return left.delta - right.delta;
    });

  let activeCount = 0;
  let maxActiveCount = 0;
  events.forEach((event) => {
    activeCount += event.delta;
    maxActiveCount = Math.max(maxActiveCount, activeCount);
  });

  return maxActiveCount;
};

export const wouldExceedConcurrentVisualStepLimit = (steps = [], stepType = "") =>
  CONCURRENT_VISUAL_STEP_TYPES.includes(stepType)
  && getMaxConcurrentStepsForType(steps, stepType) > MAX_CONCURRENT_SEQUENCE_STEPS_PER_VISUAL_TYPE;

export const getForcedStopWarning = (step = {}, steps = []) => {
  if (step?.type !== AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC || !isStepOpenEnded(step)) {
    return "";
  }

  const nextMusicStep = getNextStepOfSameType(step, steps);
  if (!nextMusicStep) {
    return "";
  }

  return `Music will continue until you stop it or until another Auto Music routine in this sequence starts at ${formatTimelineOffset(
    getStepStartOffsetSeconds(nextMusicStep)
  )}.`;
};

export const getStepVisualInterval = (step = {}, steps = []) => {
  if (!isVisualAutomationStep(step)) {
    return null;
  }

  const startOffsetSeconds = getStepStartOffsetSeconds(step);
  const effectiveEndOffsetSeconds = getStepEffectiveEndOffsetSeconds(step, steps);
  const endOffsetSeconds = Number.isFinite(effectiveEndOffsetSeconds)
    ? Math.min(TIMELINE_MAX_ABSOLUTE_OFFSET_SECONDS, effectiveEndOffsetSeconds)
    : Math.min(
      TIMELINE_MAX_ABSOLUTE_OFFSET_SECONDS,
      startOffsetSeconds + getTimelineClipDurationSeconds(step, steps)
    );

  if (endOffsetSeconds <= startOffsetSeconds) {
    return null;
  }

  return {
    startOffsetSeconds,
    endOffsetSeconds,
  };
};

export const describeVisualOverlapMoment = (overlapMoment = {}) => {
  const label = (Array.isArray(overlapMoment?.steps) ? overlapMoment.steps : [])
    .map((step) => getStepTypeMeta(step.type).label)
    .join(", ");

  return `${label} are on screen together from ${formatTimelineOffset(
    overlapMoment.startOffsetSeconds
  )} to ${formatTimelineOffset(overlapMoment.endOffsetSeconds)}.`;
};

const buildAutomationSequenceVisualOverlapMoments = (sequence = {}) => {
  const normalizedSequence = normalizeAutomationSequence(sequence);
  const sortedSteps = [...normalizedSequence.steps].sort(
    (leftStep, rightStep) => getStepStartOffsetSeconds(leftStep) - getStepStartOffsetSeconds(rightStep)
  );
  const visualSteps = sortedSteps
    .map((step) => {
      const interval = getStepVisualInterval(step, sortedSteps);
      if (!interval) {
        return null;
      }

      return {
        ...step,
        ...interval,
      };
    })
    .filter(Boolean);

  if (visualSteps.length < 2) {
    return [];
  }

  const boundaries = Array.from(
    new Set(
      visualSteps.flatMap((step) => [step.startOffsetSeconds, step.endOffsetSeconds])
    )
  ).sort((leftValue, rightValue) => leftValue - rightValue);

  const overlapMoments = [];

  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const startOffsetSeconds = boundaries[index];
    const endOffsetSeconds = boundaries[index + 1];
    const activeSteps = visualSteps.filter(
      (step) => step.startOffsetSeconds < endOffsetSeconds && step.endOffsetSeconds > startOffsetSeconds
    );

    if (activeSteps.length < 2) {
      continue;
    }

    const stepIds = activeSteps.map((step) => step.id).sort();
    const existingMoment = overlapMoments[overlapMoments.length - 1];

    if (
      existingMoment &&
      existingMoment.endOffsetSeconds === startOffsetSeconds &&
      existingMoment.stepIds.join("|") === stepIds.join("|")
    ) {
      existingMoment.endOffsetSeconds = endOffsetSeconds;
      continue;
    }

    overlapMoments.push({
      id: `visual-overlap-${stepIds.join("-")}-${startOffsetSeconds}`,
      startOffsetSeconds,
      endOffsetSeconds,
      stepIds,
      steps: activeSteps.map((step) => ({
        ...step,
        layout: getStepVisualLayout(step),
      })),
    });
  }

  return overlapMoments.map((overlapMoment) => ({
    ...overlapMoment,
    summary: describeVisualOverlapMoment(overlapMoment),
  }));
};

const doStepIdsOverlap = (firstStepIds = [], secondStepIds = []) => {
  const secondStepIdsSet = new Set(secondStepIds);
  return firstStepIds.some((stepId) => secondStepIdsSet.has(stepId));
};

export const describeVisualOverlapGroup = (overlapGroup = {}) => {
  const label = (Array.isArray(overlapGroup?.steps) ? overlapGroup.steps : [])
    .map((step) => getStepTypeMeta(step.type).label)
    .join(", ");
  const phaseCount = Array.isArray(overlapGroup?.phases) ? overlapGroup.phases.length : 0;

  return `${label} overlap from ${formatTimelineOffset(
    overlapGroup.startOffsetSeconds
  )} to ${formatTimelineOffset(overlapGroup.endOffsetSeconds)}${
    phaseCount > 1 ? ` across ${phaseCount} phases` : ""
  }`;
};

export const buildAutomationSequenceVisualOverlapGroups = (sequence = {}) => {
  const overlapMoments = buildAutomationSequenceVisualOverlapMoments(sequence);

  if (!overlapMoments.length) {
    return [];
  }

  const overlapGroups = [];

  overlapMoments.forEach((overlapMoment) => {
    const previousGroup = overlapGroups[overlapGroups.length - 1];
    const canMergeIntoPreviousGroup = Boolean(
      previousGroup &&
      previousGroup.endOffsetSeconds === overlapMoment.startOffsetSeconds &&
      doStepIdsOverlap(previousGroup.stepIds, overlapMoment.stepIds)
    );

    if (!canMergeIntoPreviousGroup) {
      overlapGroups.push({
        id: `visual-overlap-group-${overlapMoment.startOffsetSeconds}-${overlapMoment.stepIds.join("-")}`,
        startOffsetSeconds: overlapMoment.startOffsetSeconds,
        endOffsetSeconds: overlapMoment.endOffsetSeconds,
        stepIds: [...overlapMoment.stepIds],
        steps: [...overlapMoment.steps],
        phases: [overlapMoment],
      });
      return;
    }

    const mergedStepsById = [...previousGroup.steps, ...overlapMoment.steps].reduce((accumulator, step) => {
      if (!accumulator[step.id]) {
        accumulator[step.id] = step;
      }
      return accumulator;
    }, {});
    const mergedSteps = Object.values(mergedStepsById).sort((leftStep, rightStep) => {
      const startDifference = getStepStartOffsetSeconds(leftStep) - getStepStartOffsetSeconds(rightStep);
      if (startDifference !== 0) {
        return startDifference;
      }

      return leftStep.title.localeCompare(rightStep.title);
    });

    previousGroup.endOffsetSeconds = overlapMoment.endOffsetSeconds;
    previousGroup.steps = mergedSteps;
    previousGroup.stepIds = mergedSteps.map((step) => step.id);
    previousGroup.phases.push(overlapMoment);
  });

  return overlapGroups.map((overlapGroup) => ({
    ...overlapGroup,
    summary: describeVisualOverlapGroup(overlapGroup),
  }));
};

export const setStepTiming = (step = {}, timingPatch = {}) => {
  const nextDuration = timingPatch.durationSeconds == null
    ? getStepDurationSeconds(step)
    : Math.max(TIMELINE_MIN_DURATION_SECONDS, Number(timingPatch.durationSeconds) || 0);
  const nextStartOffset = timingPatch.startOffsetSeconds == null
    ? getStepStartOffsetSeconds(step)
    : clampTimelineStartOffsetSeconds(timingPatch.startOffsetSeconds);
  const nextLanePreference = timingPatch.lanePreference === undefined
    ? getExplicitLanePreference(step)
    : (timingPatch.lanePreference == null ? null : Math.max(0, Number(timingPatch.lanePreference) || 0));
  const nextLaneSubPreference = timingPatch.laneSubPreference === undefined
    ? getExplicitLaneSubPreference(step)
    : (timingPatch.laneSubPreference == null ? null : Math.max(0, Number(timingPatch.laneSubPreference) || 0));

  return {
    ...step,
    startOffsetSeconds: nextStartOffset,
    durationSeconds: nextDuration,
    lanePreference: nextLanePreference,
    laneSubPreference: nextLaneSubPreference,
    config: syncConfigDurationWithClipDuration(step?.type, step?.config, nextDuration),
  };
};

export const formatTimelineDuration = (totalSeconds) => {
  const safeTotalSeconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const hours = Math.floor(safeTotalSeconds / 3600);
  const minutes = Math.floor((safeTotalSeconds % 3600) / 60);
  const seconds = safeTotalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const formatTimelineOffset = (totalSeconds) => {
  const roundedSeconds = Math.round(Number(totalSeconds) || 0);
  const prefix = roundedSeconds > 0 ? "+" : "-";
  if (roundedSeconds === 0) {
    return "T+0:00";
  }

  return `T${prefix}${formatTimelineDuration(Math.abs(roundedSeconds))}`;
};

export const getTimelineScaleConfig = (pixelsPerSecond) => {
  const safePixelsPerSecond = Math.max(TIMELINE_MIN_PIXELS_PER_SECOND, Number(pixelsPerSecond) || TIMELINE_DEFAULT_PIXELS_PER_SECOND);

  if (safePixelsPerSecond >= 1.5) {
    return {
      minorTickSeconds: 60,
      majorTickSeconds: 300,
    };
  }

  if (safePixelsPerSecond >= 0.9) {
    return {
      minorTickSeconds: 60,
      majorTickSeconds: 300,
    };
  }

  return {
    minorTickSeconds: 300,
    majorTickSeconds: 900,
  };
};

const getNormalizedTriggerMode = (trigger = {}) =>
  ["classRelative", "dateRules", "fixed"].includes(trigger?.triggerMode)
    ? trigger.triggerMode
    : "classRelative";

const getNormalizedTriggerReference = (trigger = {}) =>
  trigger?.relativeConfig?.reference === "classEnd" ? "classEnd" : "classStart";

export const isEndOfClassRoutineTrigger = (trigger = {}) =>
  getNormalizedTriggerMode(trigger) === "classRelative"
  && getNormalizedTriggerReference(trigger) === "classEnd";

export const isLateAfterClassEndStart = (startOffsetSeconds, thresholdSeconds = LATE_AFTER_CLASS_END_WARNING_SECONDS) =>
  (Number(startOffsetSeconds) || 0) >= (Number(thresholdSeconds) || LATE_AFTER_CLASS_END_WARNING_SECONDS);

export const getLateAfterClassEndWarning = ({
  trigger = {},
  startOffsetSeconds = 0,
  thresholdSeconds = LATE_AFTER_CLASS_END_WARNING_SECONDS,
} = {}) => {
  if (!isEndOfClassRoutineTrigger(trigger)) {
    return "";
  }

  const offsetSeconds = Number(startOffsetSeconds) || 0;
  if (!isLateAfterClassEndStart(offsetSeconds, thresholdSeconds)) {
    return "";
  }

  const roundedMinutes = Math.max(1, Math.round(offsetSeconds / 60));
  const durationLabel = `${roundedMinutes} ${roundedMinutes === 1 ? "minute" : "minutes"}`;

  return `This action starts ${durationLabel} after class ends. End-of-class routines usually run at or just before dismissal — make sure you meant after class ends rather than before.`;
};

export const getTriggerTimelineLabel = (trigger = {}, options = {}) => {
  const triggerMode = getNormalizedTriggerMode(trigger);

  if (triggerMode === "classRelative") {
    return getNormalizedTriggerReference(trigger) === "classEnd"
      ? "End of Class Routine"
      : "Start of Class Routine";
  }

  if (triggerMode === "dateRules") {
    return "Custom Time Routine";
  }

  return "Scheduled Routine";
};

export const getRoutineTimelineContextLabels = (trigger = {}, options = {}) => {
  const triggerMode = getNormalizedTriggerMode(trigger);
  const passingPeriodSide = options?.passingPeriodSide === "after"
    ? "after"
    : options?.passingPeriodSide === "before"
      ? "before"
      : "";

  if (triggerMode !== "classRelative") {
    return {
      beforeLabel: "Before scheduled time",
      afterLabel: "After scheduled time",
      beforeTone: "scheduled",
      afterTone: "scheduled",
    };
  }

  if (getNormalizedTriggerReference(trigger) === "classEnd") {
    return {
      beforeLabel: "During class",
      afterLabel: passingPeriodSide === "after" ? "After class / passing period" : "After class",
      beforeTone: "during",
      afterTone: "not-during",
    };
  }

  return {
    beforeLabel: passingPeriodSide === "before" ? "Before class / passing period" : "Before class",
    afterLabel: "During class",
    beforeTone: "not-during",
    afterTone: "during",
  };
};

export const getRoutineTriggerTimelinePopoverCopy = (trigger = {}) => {
  const triggerMode = getNormalizedTriggerMode(trigger);

  if (triggerMode !== "classRelative") {
    return "Times to the left are times before your scheduled time. Times to the right are times after your scheduled time.";
  }

  if (getNormalizedTriggerReference(trigger) === "classEnd") {
    return "Times to the left are times before your class end time. Times to the right are times after your class end time.";
  }

  return "Times to the left are times before your class start time. Times to the right are times after your class start time.";
};

const packConcurrentTypeSubLanes = (stepsForType = []) => {
  const laneEndOffsets = [];
  const assignments = new Map();

  stepsForType
    .map((step, originalIndex) => ({ step, originalIndex }))
    .sort((leftEntry, rightEntry) => {
      const startDifference = getStepStartOffsetSeconds(leftEntry.step) - getStepStartOffsetSeconds(rightEntry.step);
      if (startDifference !== 0) return startDifference;
      return leftEntry.originalIndex - rightEntry.originalIndex;
    })
    .forEach(({ step }) => {
      const start = getStepStartOffsetSeconds(step);
      const end = start + getTimelineClipDurationSeconds(step, stepsForType);
      const preferredLaneIndex = getExplicitLaneSubPreference(step);
      const preferredLaneIsAvailable = preferredLaneIndex != null
        && (laneEndOffsets[preferredLaneIndex] == null || laneEndOffsets[preferredLaneIndex] <= start);
      const reusableLaneIndex = laneEndOffsets.findIndex((laneEndOffset) => laneEndOffset <= start);
      const subLaneIndex = preferredLaneIsAvailable
        ? preferredLaneIndex
        : (reusableLaneIndex >= 0 ? reusableLaneIndex : laneEndOffsets.length);

      laneEndOffsets[subLaneIndex] = end;
      assignments.set(step.id || step, subLaneIndex);
    });

  return {
    assignments,
    laneCount: Math.max(1, laneEndOffsets.length),
  };
};

export const buildSequenceTimelineLayout = (steps = []) => {
  const normalizedSteps = [...steps]
    .map((step, originalIndex) => ({
      step,
      originalIndex,
    }))
    .sort((leftEntry, rightEntry) => {
      const startDifference = getStepStartOffsetSeconds(leftEntry.step) - getStepStartOffsetSeconds(rightEntry.step);
      if (startDifference !== 0) {
        return startDifference;
      }

      return leftEntry.originalIndex - rightEntry.originalIndex;
    })
    .map((entry) => entry.step);

  const concurrentLanePackByType = CONCURRENT_VISUAL_STEP_TYPES.reduce((accumulator, stepType) => {
    const stepsForType = normalizedSteps.filter((step) => step.type === stepType);
    accumulator[stepType] = packConcurrentTypeSubLanes(stepsForType);
    return accumulator;
  }, {});
  const laneGroupSizeByType = AUTOMATION_SEQUENCE_TIMELINE_LANES.reduce((accumulator, lane) => {
    accumulator[lane.type] = concurrentLanePackByType[lane.type]?.laneCount || 1;
    return accumulator;
  }, {});
  let nextLaneBaseIndex = 0;
  const laneBaseIndexByType = AUTOMATION_SEQUENCE_TIMELINE_LANES.reduce((accumulator, lane) => {
    accumulator[lane.type] = nextLaneBaseIndex;
    nextLaneBaseIndex += laneGroupSizeByType[lane.type] || 1;
    return accumulator;
  }, {});

  return normalizedSteps.map((step) => {
    const start = getStepStartOffsetSeconds(step);
    const clipDurationSeconds = getTimelineClipDurationSeconds(step, normalizedSteps);
    const end = start + clipDurationSeconds;
    const laneMeta = getTimelineLaneMetaForType(step.type);
    const laneSubIndex = concurrentLanePackByType[step.type]?.assignments?.get(step.id || step) || 0;
    const laneGroupSize = laneGroupSizeByType[step.type] || 1;
    const laneBaseIndex = Number.isInteger(laneBaseIndexByType[step.type])
      ? laneBaseIndexByType[step.type]
      : laneMeta.index;
    const laneIndex = laneBaseIndex + laneSubIndex;

    return {
      ...step,
      startOffsetSeconds: start,
      durationSeconds: getStepDurationSeconds(step),
      clipDurationSeconds,
      endOffsetSeconds: end,
      lanePreference: laneIndex,
      laneIndex,
      laneLabel: laneMeta.label,
      laneType: step.type,
      laneBaseIndex,
      laneGroupSize,
      laneSubIndex,
      isOpenEnded: isStepOpenEnded(step),
      forcedStopWarning: getForcedStopWarning(step, normalizedSteps),
    };
  });
};

export const createSequenceStep = (type, options = {}) => {
  const config = createDefaultStepConfig(type, options);
  const nextStep = {
    id: createId(`sequence-step-${type}`),
    type,
    title: buildStepTitle(type),
    summary: buildStepSummary(type, config, options),
    config,
  };

  if ("startOffsetSeconds" in options || "durationSeconds" in options) {
    return setStepTiming({
      ...nextStep,
      startOffsetSeconds: options?.startOffsetSeconds,
      durationSeconds: options?.durationSeconds,
      lanePreference: options?.lanePreference,
      laneSubPreference: options?.laneSubPreference,
    });
  }

  return nextStep;
};

export const createAutomationSequence = (options = {}) => {
  const starterSteps = options?.steps || [
    createSequenceStep(AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC, options),
    createSequenceStep(AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN, options),
    createSequenceStep(AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER, options),
  ];

  return {
    id: options.id || createId("automation-sequence"),
    name: options.name || "New Automated Routine",
    description: options.description || "A classroom routine that runs multiple automation actions together.",
    status: options.status === "paused" ? "paused" : "active",
    version: 1,
    createdAt: options.createdAt || new Date().toISOString(),
    updatedAt: options.updatedAt || new Date().toISOString(),
    trigger: normalizeSequenceTrigger(options.trigger || createDefaultTrigger()),
    steps: starterSteps.map((step) => normalizeSequenceStep(step, options)),
  };
};

export const removeStepFromAutomationSequence = (sequence = {}, stepId = "") => ({
  ...sequence,
  steps: Array.isArray(sequence?.steps)
    ? sequence.steps.filter((step) => step?.id !== stepId)
    : [],
});

export const normalizeSequenceTrigger = (trigger = {}) => {
  const normalizedMode = getNormalizedTriggerMode(trigger);
  const classIds = Array.isArray(trigger?.relativeConfig?.classIds)
    ? trigger.relativeConfig.classIds.filter(Boolean).map(String)
    : [];

  return {
    triggerMode: normalizedMode,
    schedule: trigger?.schedule ? cloneJson(trigger.schedule) : SHARED_BLANK_SCHEDULE,
    dateRules: normalizeDateRules(trigger?.dateRules),
    relativeConfig: {
      reference: getNormalizedTriggerReference(trigger),
      direction: trigger?.relativeConfig?.direction === "after" ? "after" : "before",
      offsetSeconds: Math.max(0, Number(trigger?.relativeConfig?.offsetSeconds) || 0),
      classIds,
      classFilters: normalizeClassRelativeFilters(trigger?.relativeConfig?.classFilters, classIds),
      globalBlackoutDates: normalizeBlackoutDateConfig(trigger?.relativeConfig?.globalBlackoutDates),
    },
  };
};

export const getSignedClassRelativeTriggerOffsetSeconds = (trigger = {}) => {
  const normalizedTrigger = normalizeSequenceTrigger(trigger);
  const offsetSeconds = Math.max(0, Number(normalizedTrigger.relativeConfig.offsetSeconds) || 0);
  return normalizedTrigger.relativeConfig.direction === "before" ? -offsetSeconds : offsetSeconds;
};

export const getEarliestSequenceStepStartOffsetSeconds = (steps = []) => {
  const stepOffsets = (Array.isArray(steps) ? steps : [])
    .map((step) => getStepStartOffsetSeconds(step))
    .filter((offsetSeconds) => Number.isFinite(offsetSeconds));

  return stepOffsets.length ? Math.min(...stepOffsets) : 0;
};

export const syncClassRelativeTriggerOffsetWithTimeline = (sequence = {}) => {
  const normalizedTrigger = normalizeSequenceTrigger(sequence?.trigger);
  if (normalizedTrigger.triggerMode !== "classRelative") {
    return sequence;
  }

  const earliestStepOffsetSeconds = getEarliestSequenceStepStartOffsetSeconds(sequence?.steps);
  const direction = earliestStepOffsetSeconds < 0
    ? "before"
    : earliestStepOffsetSeconds > 0
      ? "after"
      : normalizedTrigger.relativeConfig.direction;

  return {
    ...sequence,
    trigger: {
      ...normalizedTrigger,
      relativeConfig: {
        ...normalizedTrigger.relativeConfig,
        direction,
        offsetSeconds: Math.abs(Math.round(earliestStepOffsetSeconds)),
      },
    },
  };
};

export const normalizeSequenceStep = (step = {}, options = {}) => {
  const type = STEP_LIBRARY.some((entry) => entry.type === step?.type)
    ? step.type
    : AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC;
  const baseConfig = {
    ...createDefaultStepConfig(type, options),
    ...(step?.config || {}),
  };
  const durationSeconds = getExplicitStepDuration(step) == null
    ? getDefaultDurationForType(type, baseConfig)
    : getExplicitStepDuration(step);
  const syncedConfig = syncConfigDurationWithClipDuration(type, baseConfig, durationSeconds);
  const config = normalizeVisualLayoutConfig(
    type,
    type === AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE
      ? normalizeAutomationSequenceResourceConfig(syncedConfig)
      : type === AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY
        ? normalizeBirthdaySoundConfig(syncedConfig)
        : syncedConfig
  );

  return setStepTiming({
    id: step?.id || createId(`sequence-step-${type}`),
    type,
    title: step?.title || buildStepTitle(type),
    summary: buildStepSummary(type, config, options),
    config,
    classOverrides: normalizeStepClassOverrides(step?.classOverrides),
    startOffsetSeconds: getExplicitStepStartOffset(step),
    durationSeconds,
    lanePreference: getExplicitLanePreference(step),
    laneSubPreference: getExplicitLaneSubPreference(step),
  });
};

export const resolveStepForClass = (step = {}, classId = "", options = {}) => {
  const normalizedStep = normalizeSequenceStep(step, options);
  const normalizedClassId = String(classId || "");
  const override = normalizeStepClassOverrides(normalizedStep.classOverrides)[normalizedClassId];
  if (!override) {
    return normalizedStep;
  }

  const overrideConfig = override.config || {};
  const nextStep = {
    ...normalizedStep,
    config: normalizeVisualLayoutConfig(normalizedStep.type, {
      ...normalizedStep.config,
      ...overrideConfig,
    }),
  };

  const hasTimingOverride = Object.keys(override.timing || {}).length > 0;
  return hasTimingOverride
    ? setStepTiming(nextStep, override.timing)
    : setStepTiming(nextStep);
};

export const normalizeAutomationSequence = (sequence = {}, options = {}) => {
  const rawSteps = (Array.isArray(sequence?.steps) ? sequence.steps : []).filter(
    (rawStep) => rawStep?.type !== AUTOMATION_SEQUENCE_STEP_TYPES.WAIT
  );
  let runningOffsetSeconds = 0;
  const steps = rawSteps.map((rawStep) => {
    const normalizedStep = normalizeSequenceStep(rawStep, options);
    const explicitOffset = getExplicitStepStartOffset(rawStep);
    const nextStep = setStepTiming(normalizedStep, {
      startOffsetSeconds: explicitOffset == null ? runningOffsetSeconds : explicitOffset,
    });

    if (explicitOffset == null) {
      runningOffsetSeconds += getStepDurationSeconds(nextStep);
    }

    return nextStep;
  });

  const shouldNormalizeName = options.normalizeName !== false;
  let name = "Untitled Routine";
  if (typeof sequence?.name === "string") {
    name = shouldNormalizeName
      ? (sequence.name.trim() || "Untitled Routine")
      : sequence.name;
  }

  return {
    id: sequence?.id || createId("automation-sequence"),
    name,
    description: typeof sequence?.description === "string" ? sequence.description : "",
    status: AUTOMATION_SEQUENCE_STATUS_OPTIONS.some((statusOption) => statusOption.value === sequence?.status)
      ? sequence.status
      : "active",
    version: Number.isFinite(Number(sequence?.version)) ? Number(sequence.version) : 1,
    createdAt: sequence?.createdAt || new Date().toISOString(),
    updatedAt: sequence?.updatedAt || new Date().toISOString(),
    trigger: normalizeSequenceTrigger(sequence?.trigger),
    steps,
  };
};

export const getTriggerSummary = (trigger = {}, options = {}) => {
  const classesById = options?.classesById || {};
  const normalizedTrigger = normalizeSequenceTrigger(trigger);

  if (normalizedTrigger.triggerMode === "classRelative") {
    const classLabels = normalizedTrigger.relativeConfig.classIds
      .map((classId) => classesById[classId]?.className || classId)
      .filter(Boolean);
    const referenceLabel = normalizedTrigger.relativeConfig.reference === "classEnd" ? "End of Class Routine" : "Start of Class Routine";
    const classSummary = classLabels.length > 0 ? classLabels.join(", ") : "selected classes";
    const filteredClassCount = Object.values(normalizedTrigger.relativeConfig.classFilters || {})
      .filter((filter) => filter.scheduleMode === "selected").length;
    const classBlackoutCount = Object.values(normalizedTrigger.relativeConfig.classFilters || {})
      .filter((filter) => hasBlackoutDateConfig(filter.blackoutDates)).length;
    const hasGlobalBlackouts = hasBlackoutDateConfig(normalizedTrigger.relativeConfig.globalBlackoutDates);
    const extras = [
      filteredClassCount ? `${filteredClassCount} schedule filter${filteredClassCount === 1 ? "" : "s"}` : "",
      hasGlobalBlackouts || classBlackoutCount ? "blackout dates" : "",
    ].filter(Boolean);

    return `${referenceLabel} for ${classSummary}${extras.length ? ` • ${extras.join(" • ")}` : ""}`;
  }

  if (normalizedTrigger.triggerMode === "dateRules") {
    return normalizedTrigger.dateRules.length > 0
      ? `${normalizedTrigger.dateRules.length} custom time${normalizedTrigger.dateRules.length === 1 ? "" : "s"} configured`
      : "No custom times configured";
  }

  const activeEntries = getActiveScheduleEntryCount(normalizedTrigger.schedule);
  return `${activeEntries} active fixed schedule slot${activeEntries === 1 ? "" : "s"}`;
};

const AUTOMATION_SEQUENCE_PREVIEW_NUMBER_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];
const AUTOMATION_SEQUENCE_PREVIEW_TENS_WORDS = {
  20: "twenty",
  30: "thirty",
  40: "forty",
  50: "fifty",
};

const formatPreviewSmallNumberWords = (value) => {
  const numberValue = Math.max(0, Math.floor(Number(value) || 0));
  if (numberValue < AUTOMATION_SEQUENCE_PREVIEW_NUMBER_WORDS.length) return AUTOMATION_SEQUENCE_PREVIEW_NUMBER_WORDS[numberValue];
  if (numberValue < 60) {
    const tensValue = Math.floor(numberValue / 10) * 10;
    const onesValue = numberValue % 10;
    return onesValue
      ? `${AUTOMATION_SEQUENCE_PREVIEW_TENS_WORDS[tensValue]}-${AUTOMATION_SEQUENCE_PREVIEW_NUMBER_WORDS[onesValue]}`
      : AUTOMATION_SEQUENCE_PREVIEW_TENS_WORDS[tensValue];
  }
  return String(numberValue);
};

const formatPreviewFriendlyDuration = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  const parts = [];

  if (minutes > 0) {
    parts.push(`${formatPreviewSmallNumberWords(minutes)} ${minutes === 1 ? "minute" : "minutes"}`);
  }
  if (remainingSeconds > 0 || parts.length === 0) {
    parts.push(`${formatPreviewSmallNumberWords(remainingSeconds)} ${remainingSeconds === 1 ? "second" : "seconds"}`);
  }

  return parts.join(" and ");
};

const getPreviewTriggerReferenceLabel = (trigger = {}) => {
  const normalizedTrigger = normalizeSequenceTrigger(trigger);
  if (normalizedTrigger.triggerMode !== "classRelative") {
    return "routine time";
  }

  return normalizedTrigger.relativeConfig.reference === "classEnd" ? "end of class" : "start of class";
};

const formatPreviewSentenceStart = (startOffsetSeconds, triggerReferenceLabel) => {
  const offsetSeconds = Number(startOffsetSeconds) || 0;
  if (offsetSeconds === 0) {
    return `At the ${triggerReferenceLabel}`;
  }

  const relation = offsetSeconds < 0 ? "before" : "after";
  const duration = formatPreviewFriendlyDuration(Math.abs(offsetSeconds));
  return `${duration.charAt(0).toUpperCase()}${duration.slice(1)} ${relation} the ${triggerReferenceLabel}`;
};

const quotePreviewText = (value, fallback) => {
  const text = String(value || "").trim() || fallback;
  return `"${text}"`;
};

const buildStepPreviewSummary = (step = {}, options = {}) => {
  const playlistsById = options?.playlistsById || {};
  const triggerReferenceLabel = options?.triggerReferenceLabel || "routine time";
  const sentenceStart = formatPreviewSentenceStart(step.startOffsetSeconds, triggerReferenceLabel);

  switch (step.type) {
    case AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN: {
      const countdownMessage = quotePreviewText(step.config?.header, "Countdown");
      const countdownDuration = formatPreviewFriendlyDuration(step.durationSeconds);
      const endMessage = String(step.config?.alertMessage || "").trim();
      const endMessageSentence = endMessage
        ? ` At the end of the countdown, the message will show ${quotePreviewText(endMessage)}.`
        : "";

      return `${sentenceStart} a timer will launch that says ${countdownMessage} and count down for ${countdownDuration}.${endMessageSentence}`;
    }
    case AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER:
      return `${sentenceStart} a reminder will launch that says ${quotePreviewText(step.config?.reminderMessage, "Show reminder")}.`;
    case AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY:
      return `${sentenceStart} the birthday celebration will launch${
        resolveBirthdayAudioSource(step.config).type !== BIRTHDAY_AUDIO_SOURCE_TYPES.NONE
          ? " with birthday music"
          : ""
      }.`;
    case AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE: {
      const resource = automationSequenceResourceConfigToQuickLaunchResource(step);
      const launchTarget = resource?.openTarget === QUICK_LAUNCH_OPEN_TARGETS.NEW_TAB
        ? "in a new browser tab"
        : "in Lesson Launchpad";
      return `${sentenceStart} ${resource?.title || "a resource"} will open ${launchTarget}.`;
    }
    case AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC:
      return `${sentenceStart} music will start. ${buildStepSummary(step.type, step.config, { playlistsById })}.`;
    case AUTOMATION_SEQUENCE_STEP_TYPES.WAIT:
      return `${sentenceStart} the routine will wait for ${formatPreviewFriendlyDuration(step.config?.duration)}.`;
    default:
      return `${sentenceStart} ${buildStepSummary(step.type, step.config, { playlistsById })}.`;
  }
};

export const AUTOMATION_SEQUENCE_VALIDATION_WARNING_TYPES = {
  CLASS_NOT_ON_DAILY_SCHEDULE: "class-not-on-daily-schedule",
};

export const UNSUPPORTED_FIXED_TRIGGER_CALENDAR_MESSAGE =
  "Scheduled Routine triggers are no longer supported in calendar scheduling mode. Choose Start of Class, End of Class, or Custom Time instead.";

export const buildClassNotOnDailyScheduleWarning = (className) => ({
  type: AUTOMATION_SEQUENCE_VALIDATION_WARNING_TYPES.CLASS_NOT_ON_DAILY_SCHEDULE,
  className,
  message: `${className} is not assigned to any daily schedules yet, so this routine cannot run for that class. Save this routine and go to the Schedules page to add ${className} to one of your daily schedules.`,
});

export const getValidationIssueMessage = (issue) =>
  typeof issue === "string" ? issue : issue?.message || "";

export const validationIssueHasFixAction = (issue) => {
  if (typeof issue === "string") {
    return true;
  }

  return issue?.type !== AUTOMATION_SEQUENCE_VALIDATION_WARNING_TYPES.CLASS_NOT_ON_DAILY_SCHEDULE;
};

export const validateAutomationSequence = (sequence = {}, options = {}) => {
  const rawStepsById = new Map(
    (Array.isArray(sequence?.steps) ? sequence.steps : [])
      .filter((step) => step?.id)
      .map((step) => [String(step.id), step])
  );
  const normalizedSequence = normalizeAutomationSequence(sequence);
  const errors = [];
  const warnings = [];
  const visualOverlapGroups = buildAutomationSequenceVisualOverlapGroups(normalizedSequence);

  if (!normalizedSequence.name.trim()) {
    errors.push("Routine name is required.");
  }

  if (normalizedSequence.trigger.triggerMode === "classRelative") {
    if (normalizedSequence.trigger.relativeConfig.classIds.length === 0) {
      errors.push("No classes assigned for this automated routine. Select at least one class.");
    }

    const scheduleTemplates = Array.isArray(options?.scheduleCalendar?.templates)
      ? options.scheduleCalendar.templates.filter((template) => template?.id && template?.active !== false)
      : [];
    normalizedSequence.trigger.relativeConfig.classIds.forEach((classId) => {
      const classItem = (Array.isArray(options?.classes) ? options.classes : [])
        .find((candidateClass) => String(candidateClass?.classId) === String(classId));
      const assignedTemplateIds = scheduleTemplates
        .filter((template) =>
          (Array.isArray(template?.classes) ? template.classes : []).some(
            (scheduleClass) => String(scheduleClass?.classId) === String(classId)
          )
        )
        .map((template) => String(template.id));
      const classLabel = classItem?.className || classId;

      if (options?.scheduleCalendar?.scheduleRuntimeMode === "calendar" && assignedTemplateIds.length === 0) {
        warnings.push(buildClassNotOnDailyScheduleWarning(classLabel));
      }

      const classFilter = normalizeClassRelativeFilter(normalizedSequence.trigger.relativeConfig.classFilters?.[String(classId)]);
      if (
        classFilter.scheduleMode === "selected" &&
        classFilter.allowedTemplateIds.some((templateId) => !assignedTemplateIds.includes(templateId))
      ) {
        warnings.push(`${classLabel} has a schedule filter that includes a schedule no longer assigned to that class.`);
      }
    });
  }

  if (normalizedSequence.trigger.triggerMode === "dateRules" && normalizedSequence.trigger.dateRules.length === 0) {
    errors.push("Add at least one custom time.");
  }
  if (
    normalizedSequence.trigger.triggerMode === "dateRules" &&
    normalizedSequence.steps.some((step) => step.type === AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY)
  ) {
    warnings.push("Birthday uses the class in session at each custom time. If no class is scheduled then, the birthday step skips.");
  }

  if (
    normalizedSequence.trigger.triggerMode === "fixed" &&
    options?.scheduleCalendar?.scheduleRuntimeMode === "calendar"
  ) {
    errors.push(UNSUPPORTED_FIXED_TRIGGER_CALENDAR_MESSAGE);
  }

  if (normalizedSequence.trigger.triggerMode === "fixed" && getActiveScheduleEntryCount(normalizedSequence.trigger.schedule) === 0) {
    errors.push("Activate at least one scheduled time for this routine.");
  }

  if (normalizedSequence.steps.length === 0) {
    warnings.push("This routine has no actions yet.");
  }

  const resourceStepCount = normalizedSequence.steps.filter(
    (step) => step.type === AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE
  ).length;
  if (resourceStepCount > MAX_RESOURCE_STEPS_PER_SEQUENCE) {
    errors.push(`Routines can launch up to ${MAX_RESOURCE_STEPS_PER_SEQUENCE} resources.`);
  }

  const selectedClassIdSet = new Set(normalizedSequence.trigger.relativeConfig.classIds.map(String));
  normalizedSequence.steps.forEach((step) => {
    Object.keys(normalizeStepClassOverrides(step.classOverrides)).forEach((classId) => {
      if (normalizedSequence.trigger.triggerMode !== "classRelative" || !selectedClassIdSet.has(String(classId))) {
        warnings.push(`${step.title} has class overrides for a class that is not assigned to this routine.`);
      }
    });
  });

  const sortedSteps = [...normalizedSequence.steps].sort(
    (leftStep, rightStep) => getStepStartOffsetSeconds(leftStep) - getStepStartOffsetSeconds(rightStep)
  );

  Object.values(AUTOMATION_SEQUENCE_STEP_TYPES)
    .filter((stepType) => stepType !== AUTOMATION_SEQUENCE_STEP_TYPES.WAIT)
    .forEach((stepType) => {
      const stepsForType = sortedSteps
        .filter((step) => step.type === stepType)
        .sort((leftStep, rightStep) => getStepStartOffsetSeconds(leftStep) - getStepStartOffsetSeconds(rightStep));

      if (CONCURRENT_VISUAL_STEP_TYPES.includes(stepType)) {
        if (getMaxConcurrentStepsForType(stepsForType, stepType) > MAX_CONCURRENT_SEQUENCE_STEPS_PER_VISUAL_TYPE) {
          errors.push(
            `${getStepTypeMeta(stepType).label} blocks can overlap, but no more than ${MAX_CONCURRENT_SEQUENCE_STEPS_PER_VISUAL_TYPE} can be visible at once.`
          );
        }
        return;
      }

      for (let index = 1; index < stepsForType.length; index += 1) {
        const previousStep = stepsForType[index - 1];
        const currentStep = stepsForType[index];
        const previousEnd = getStepEffectiveEndOffsetSeconds(previousStep, stepsForType);

        if (getStepStartOffsetSeconds(currentStep) < previousEnd) {
          errors.push(
            `${getStepTypeMeta(stepType).label} blocks cannot overlap.`
          );
          break;
        }
      }
    });

  sortedSteps.forEach((step, index) => {
    const prefix = `Step ${index + 1} (${step.title} at ${formatTimelineOffset(step.startOffsetSeconds)})`;
    const effectiveEndOffsetSeconds = getStepEffectiveEndOffsetSeconds(step, sortedSteps);

    if (
      isEndOfClassRoutineTrigger(normalizedSequence.trigger)
      && isLateAfterClassEndStart(getStepStartOffsetSeconds(step))
    ) {
      warnings.push(
        `${prefix} starts well after class ends. End-of-class routines usually run at or just before dismissal — double-check that this later start time is intentional.`
      );
    }

    if (getStepStartOffsetSeconds(step) < -TIMELINE_MAX_ABSOLUTE_OFFSET_SECONDS) {
      errors.push(`${prefix} cannot start earlier than ${formatTimelineOffset(-TIMELINE_MAX_ABSOLUTE_OFFSET_SECONDS)}.`);
    }

    if (Number.isFinite(effectiveEndOffsetSeconds) && effectiveEndOffsetSeconds > TIMELINE_MAX_ABSOLUTE_OFFSET_SECONDS) {
      errors.push(`${prefix} cannot extend later than ${formatTimelineOffset(TIMELINE_MAX_ABSOLUTE_OFFSET_SECONDS)}.`);
    }

    if (!isStepOpenEnded(step) && getStepDurationSeconds(step) < TIMELINE_MIN_DURATION_SECONDS) {
      errors.push(`${prefix} needs a clip duration of at least ${TIMELINE_MIN_DURATION_SECONDS} seconds.`);
    }

    switch (step.type) {
      case AUTOMATION_SEQUENCE_STEP_TYPES.WAIT:
        if ((Number(step.config?.duration) || 0) <= 0) {
          errors.push(`${prefix} must wait for at least 1 second.`);
        }
        break;
      case AUTOMATION_SEQUENCE_STEP_TYPES.MUSIC:
        if (!step.config?.playlistId) {
          errors.push(`${prefix} needs a playlist selection.`);
        }
        if (getForcedStopWarning(step, sortedSteps)) {
          warnings.push(`${prefix} will stop when the next Auto Music routine in this sequence starts.`);
        }
        if (
          step.config?.stopMode === "duration" &&
          (Number(step.config?.duration) || 0) < TIMELINE_MIN_DURATION_SECONDS
        ) {
          errors.push(`${prefix} needs a duration of at least ${TIMELINE_MIN_DURATION_SECONDS} seconds when stop mode is set to duration.`);
        }
        break;
      case AUTOMATION_SEQUENCE_STEP_TYPES.COUNTDOWN:
        if (!step.config?.header) {
          errors.push(`${prefix} needs a countdown header.`);
        }
        if ((Number(step.config?.duration) || 0) < TIMELINE_MIN_DURATION_SECONDS) {
          errors.push(`${prefix} needs a duration of at least ${TIMELINE_MIN_DURATION_SECONDS} seconds.`);
        }
        break;
      case AUTOMATION_SEQUENCE_STEP_TYPES.REMINDER:
        if (!step.config?.reminderMessage) {
          errors.push(`${prefix} needs reminder text.`);
        }
        if (step.config?.closeMode !== "manual" && (Number(step.config?.duration) || 0) < TIMELINE_MIN_DURATION_SECONDS) {
          errors.push(`${prefix} needs a display duration of at least ${TIMELINE_MIN_DURATION_SECONDS} seconds.`);
        }
        break;
      case AUTOMATION_SEQUENCE_STEP_TYPES.BIRTHDAY:
        if (step.config?.close !== "manual" && (Number(step.config?.duration) || 0) < TIMELINE_MIN_DURATION_SECONDS) {
          errors.push(`${prefix} needs a display duration of at least ${TIMELINE_MIN_DURATION_SECONDS} seconds.`);
        }
        break;
      case AUTOMATION_SEQUENCE_STEP_TYPES.RESOURCE: {
        const rawStep = rawStepsById.get(String(step.id)) || {};
        const rawResourceType = String(rawStep?.config?.resourceType || rawStep?.config?.type || "").trim();
        if (rawResourceType && !AUTOMATION_SEQUENCE_RESOURCE_TYPES.includes(rawResourceType)) {
          errors.push(`${prefix} must be a Link or PDF resource.`);
        } else if (!automationSequenceResourceConfigToQuickLaunchResource(step)) {
          errors.push(`${prefix} needs a valid resource URL.`);
        }
        break;
      }
      default:
        warnings.push(`${prefix} uses an unknown step type.`);
        break;
    }
  });

  visualOverlapGroups.forEach((overlapGroup) => {
    const hasUnsavedDefaultLayout = (overlapGroup.steps || []).some((step) => !step.config?.layoutWasEdited);
    const layoutHint = hasUnsavedDefaultLayout
      ? " Lesson Launchpad will auto-offset unsaved default layouts at runtime, but the Layout Editor gives you the intended placement."
      : "";
    warnings.push(`You have multiple overlapping automations at once. Choose where they appear on your screen using the Layout Editor.${layoutHint} ${overlapGroup.summary}.`);
  });

  return {
    errors,
    warnings,
    visualOverlapGroups,
    isValid: errors.length === 0,
  };
};

export const compileAutomationSequencePreview = (sequence = {}, options = {}) => {
  const normalizedSequence = normalizeAutomationSequence(sequence, options);
  const playlistsById = options?.playlistsById || {};
  const triggerReferenceLabel = getPreviewTriggerReferenceLabel(normalizedSequence.trigger);
  const instructions = [];

  [...normalizedSequence.steps]
    .sort((leftStep, rightStep) => getStepStartOffsetSeconds(leftStep) - getStepStartOffsetSeconds(rightStep))
    .forEach((step) => {
    if (step.type === AUTOMATION_SEQUENCE_STEP_TYPES.WAIT) {
      instructions.push({
        id: step.id,
        kind: "delay",
        title: step.title,
        summary: buildStepPreviewSummary(step, { playlistsById, triggerReferenceLabel }),
        iconMeta: { tone: "wait", icon: "fa fa-hourglass-half" },
      });
      return;
    }

    const forcedStopWarning = getForcedStopWarning(step, normalizedSequence.steps);
    const stepTypeMeta = getStepTypeMeta(step.type);
    instructions.push({
      id: step.id,
      kind: "action",
      title: step.title,
      summary: `${buildStepPreviewSummary(step, { playlistsById, triggerReferenceLabel })}${forcedStopWarning ? " It will stop when the next Auto Music routine in this sequence starts." : ""}`,
      iconMeta: { tone: stepTypeMeta.tone, icon: stepTypeMeta.icon },
    });
    });

  const plainEnglish = instructions.map((instruction, index) =>
    `${index + 1}. ${instruction.title}: ${instruction.summary}`
  );

  return {
    instructions,
    plainEnglish,
  };
};

export const buildPlaylistsById = (playlists = []) =>
  (Array.isArray(playlists) ? playlists : []).reduce((accumulator, playlist) => {
    if (playlist?.playlistId) {
      accumulator[playlist.playlistId] = playlist;
    }
    return accumulator;
  }, {});

export const buildClassesById = (classes = []) =>
  (Array.isArray(classes) ? classes : []).reduce((accumulator, classItem) => {
    if (classItem?.classId) {
      accumulator[classItem.classId] = classItem;
    }
    return accumulator;
  }, {});
